import { inject, Injectable } from "@angular/core";
import { ShareProjectLinkModalComponent } from "@components/share-project-link-modal/share-project-link-modal.component";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { ToastHelper } from "@utils/toast-helper.service";
import { catchError, filter, first, of, switchMap, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ReadAccessModalService {
  private projectApiService = inject(ProjectApiService);
  private toastHelper = inject(ToastHelper);
  private projectService = inject(ProjectService);
  private userService = inject(UserService);

  private readonly modalService = inject(ModalService);

  public openModal(project: ScopedProject) {
    this.modalService
      .open(ShareProjectLinkModalComponent, { size: ModalSize.MEDIUM })
      .onClose$.pipe(
        filter((value) => !!value && !!project?.cloudId),
        switchMap((value) =>
          this.userService.user$.pipe(
            first(),
            switchMap((user) => this.shareProjectLinkAndHandleErrors({ ...value, language: user.language }, project)),
          ),
        ),
        switchMap((response) => this.updateProjectMetadata(response)),
      )
      .subscribe();
  }
  private shareProjectLinkAndHandleErrors(value: any, project: ScopedProject) {
    return this.projectApiService.shareProjectLink(value, project.cloudId!).pipe(
      tap((response) => this.handleShareLinkResponse(response)),
      catchError(() => {
        this.handleShareLinkError();
        return of(undefined);
      }),
    );
  }

  private updateProjectMetadata(response: any) {
    if (!response?.metadata?.id) {
      return of(undefined);
    }
    return this.projectService.updateLocalMetadata(response.metadata);
  }

  private handleShareLinkResponse(response: any) {
    const results = response?.results;
    if (!Array.isArray(results)) {
      this.showErrorToast();
      return;
    }

    const hasFailures = results.some((result: { success?: boolean }) => !result.success);
    if (hasFailures) {
      this.showErrorToast();
    } else {
      this.showSuccessToast();
    }
  }

  private handleShareLinkError() {
    this.showErrorToast();
  }

  private showErrorToast() {
    this.toastHelper.error(
      $localize`:@@toast.shareLink.error.header:Projekt konnte nicht geteilt werden`,
      $localize`:@@toast.shareLink.error.description:Bitte versuchen Sie es später erneut.`,
    );
  }

  private showSuccessToast() {
    this.toastHelper.success($localize`:@@toast.shareLink.successful:Link wurde erfolgreich verschickt`);
  }
}
